import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './OperationCards.css';
// .toggle-switch / .numeric-input / .setting-input and .preset-card live in
// these two sheets. Imported explicitly so the cards keep their controls even
// if a future change stops mounting SettingsEditor or the render dialog.
import '../SettingsEditor.css';
import '../RenderOptionsDialog.css';
import { OPERATIONS_BY_UI_ORDER, displayNameOf } from '../../lib/operations';
import {
  OPERATIONS as OPERATION_COPY,
  DEFLICKER_STRENGTHS,
  WATERMARK_POSITION_LABELS,
  MOTION_MODES,
  MOTION_SECTION,
} from '../../lib/operationCopy';
import {
  DEFLICKER_WINDOW_CHOICES,
  FLICKER_DETECT_THRESHOLD_PERCENT,
  normalizeDeflickerWindow,
} from '../../lib/flickerPreview';
import {
  transitionsEnabled,
  renderSegmentsForDraft,
  fadeBoundaryTally,
  effectiveFadeFrames,
  renderCrossfadeCount,
  MAX_CAPTURE_GAP_SPLITS,
  isAnalysisInFlight,
  WATERMARK_POSITIONS,
  WATERMARK_POSITION_DEFAULT,
  WATERMARK_SIZE_MIN,
  WATERMARK_SIZE_MAX,
  WATERMARK_SIZE_DEFAULT,
  WATERMARK_OPACITY_MIN,
  WATERMARK_OPACITY_MAX,
  WATERMARK_STOCK_OPACITY,
  WATERMARK_LOGO_MAX_BYTES,
  WATERMARK_LOGO_SLOTS,
  isWatermarkLogoId,
  firstFreeWatermarkSlot,
  watermarkSlotLabel,
  watermarkLogoLabel,
  normalizeWatermarkPosition,
  normalizeWatermarkSize,
  normalizeWatermarkOpacity,
  motionModeOf,
  motionModeChanges,
  MOTION_MODE_TRIM,
  MOTION_MODE_PACE,
} from '../../lib/editDraft';
import { normalizeGapFrames } from '../../lib/captureGaps';
import MotionSection from './MotionSection';
import { auth, storage } from '../../firebase';
import {
  ref as storageFileRef,
  listAll,
  getDownloadURL,
  getMetadata,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import { PRICING_TIERS } from '../../pricing/plans';
import {
  clamp,
  secondsToFrames,
  framesToSeconds,
  formatSeconds,
  getTransitionPresets,
  matchTransitionPreset,
} from '../../utils/settingsControls';
import { trackEvent } from '../../analytics';
import UpgradePromptDialog from '../UpgradePromptDialog';
import FlickerPreview from './FlickerPreview';
import { UPSELL_PROMPTS } from '../../upsell/triggers';
// The SAME wordmark the worker burns in
// (packages/containers/generatevideo/assets/watermark.png, copied byte for
// byte). The preview below reproduces that renderer's geometry, so what the
// user sees here is what lands in the mp4.
import watermarkAsset from '../../assets/watermark.png';

// Reading order for the corner dropdown menu (top-to-bottom on screen).
// WATERMARK_POSITIONS itself stays in its wire order — that's a serialization
// contract pinned elsewhere — this is purely a display concern.
const WATERMARK_CORNER_DISPLAY_ORDER = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

// One card per operation, in the order ffmpeg applies them (#401).
//
// The list comes from OPERATIONS_BY_UI_ORDER — the registry (#400) sorted by
// pipelinePosition — so the order on screen is the real chain order and a new
// operation slots in at its own position without anyone re-sorting a list here.
// Each card owns three things and nothing else: a state badge readable without
// expanding, a switch, and an inline panel that only shows the operation's
// controls while it is ON. When it is OFF the panel states WHY there is nothing
// to configure rather than showing controls that would not run.

const Chevron = ({ open }) => (
  <span className={`op-card__chevron${open ? ' is-open' : ''}`} aria-hidden="true">
    &rsaquo;
  </span>
);

const ToggleSwitch = ({ id, checked, disabled, onChange, label }) => (
  <label className={`toggle-switch${disabled ? ' is-locked' : ''}`}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
    />
    <span className="toggle-slider"></span>
  </label>
);

const OperationCard = ({
  operationId,
  name,
  state,
  stateTone = 'neutral',
  note,
  expanded,
  onSelect,
  toggle,
  affordance,
  rootRef,
  children,
}) => {
  const panelId = `op-panel-${operationId}`;
  return (
    <div
      className="op-card"
      ref={rootRef}
      data-testid={`op-card-${operationId}`}
      data-expanded={String(!!expanded)}
    >
      <div className="op-card__row">
        <button
          type="button"
          className="op-card__select"
          onClick={onSelect}
          aria-expanded={!!expanded}
          aria-controls={panelId}
          data-testid={`op-select-${operationId}`}
        >
          <Chevron open={expanded} />
          <span className="op-card__name">{name}</span>
          <span
            className={`op-card__state op-card__state--${stateTone}`}
            data-testid={`op-state-${operationId}`}
          >
            {state}
          </span>
          {note && (
            <span className="op-card__note" data-testid={`op-note-${operationId}`}>{note}</span>
          )}
        </button>
        {affordance}
        {toggle}
      </div>
      {expanded && (
        <div id={panelId} className="op-card__panel" data-testid={`op-panel-${operationId}`}>
          {children}
        </div>
      )}
    </div>
  );
};

// A panel that explains why an operation has nothing to configure. Rendered in
// place of the controls whenever the operation is off, which is what keeps
// "settings for something that will not run" off the screen.
const OffReason = ({ operationId, children }) => (
  <p className="op-card__reason" data-testid={`op-reason-${operationId}`}>{children}</p>
);

// One line of the Transitions card's "Fade at" checklist: a boundary type this
// project actually has, and whether the render fades there.
//
// `onChange` is OPTIONAL, and its absence is the whole point of the component.
// Motion cuts have no switch on the wire — `transition_length_frames > 0` fades
// every ordinary segment boundary unconditionally — so that row renders as a
// checked, inert box rather than a control that would imply a setting the
// render does not have. `readOnly` is what keeps React from warning about a
// checked input with no handler; `disabled` is what makes it un-clickable.
//
// The accessible name is the LABEL alone (aria-labelledby), with the count line
// attached as a description, so a screen reader announces "Camera pauses,
// checkbox" rather than the label welded to "24 gaps detected".
const FadeRow = ({ id, label, meta, checked, onChange = null }) => (
  <label
    className={`op-fade-row${onChange ? '' : ' op-fade-row--fixed'}`}
    data-testid={`fade-row-${id}`}
  >
    <input
      type="checkbox"
      className="op-fade-row__box"
      checked={checked}
      disabled={!onChange}
      readOnly={!onChange}
      onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
      aria-labelledby={`fade-row-${id}-label`}
      aria-describedby={`fade-row-${id}-meta`}
    />
    <span className="op-fade-row__text">
      <span className="op-fade-row__label" id={`fade-row-${id}-label`}>{label}</span>
      <span className="op-fade-row__meta" id={`fade-row-${id}-meta`}>{meta}</span>
    </span>
  </label>
);

// MotionPace's answer to "where do the motion cuts fade": there are none. The
// paced clip is ONE continuous segment — quiet stretches are sped through, not
// cut out — so the row above has nothing to count. Gap and join fades still
// apply, which is why this replaces one row rather than the whole checklist.
const PACE_NO_CUTS =
  'MotionPace glides through quiet stretches instead of cutting — nothing to fade there.';

// Every constant here mirrors packages/containers/generatevideo/processing/
// watermark.py — the module that actually burns the mark in. Kept in sync by
// hand; if those change, this preview lies.
const WM_PADDING_RATIO = 0.025;
const WM_MIN_WIDTH_PX = 80;
const WM_MIN_PADDING_PX = 8;
const WM_FALLBACK_ASPECT = 16 / 9;

// The renderer works in the output's real pixels: the mark spans `widthRatio`
// of frame WIDTH (14% stock, 5–30% customized) inset 2.5% of frame width from
// its corner, with 80px/8px floors that bite on small outputs. Percentages of
// the preview box only line up with that if the box carries the source's own
// aspect ratio — a 9:16 phone timelapse in a hardcoded 16:9 box misreports
// both the mark's size and where it lands.
const markGeometry = (width, height, widthRatio = WATERMARK_SIZE_DEFAULT) => {
  const known = width > 0 && height > 0;
  if (!known) {
    // No pixel size, so the floors cannot be modelled — the bare ratios are
    // what applies at every output above them.
    return {
      aspect: WM_FALLBACK_ASPECT,
      widthPct: widthRatio * 100,
      insetXPct: WM_PADDING_RATIO * 100,
      insetYPct: WM_PADDING_RATIO * WM_FALLBACK_ASPECT * 100,
    };
  }
  const markPx = Math.max(WM_MIN_WIDTH_PX, Math.round(width * widthRatio));
  const padPx = Math.max(WM_MIN_PADDING_PX, Math.round(width * WM_PADDING_RATIO));
  return {
    aspect: width / height,
    widthPct: (markPx / width) * 100,
    insetXPct: (padPx / width) * 100,
    // The vertical inset resolves against the box's height, but the renderer's
    // inset is in pixels — the same inset is a larger share of a short frame.
    insetYPct: (padPx / height) * 100,
  };
};

// A still of the user's OWN footage with the mark composited exactly where the
// renderer puts it, so the switch has something visible to change. The <video>
// is decoration: no controls, no pointer events, metadata only — it is there
// for frame one and nothing else.
//
// `logoUrl` swaps the stock wordmark for an uploaded logo; corner/size/opacity
// mirror the worker's customization knobs. When `onSelectCorner` is provided
// each corner of the frame is also a click target that picks that corner.
const WatermarkPreview = ({
  videoUrl,
  resolution,
  showMark,
  logoUrl = null,
  position = WATERMARK_POSITION_DEFAULT,
  sizeRatio = WATERMARK_SIZE_DEFAULT,
  opacity = WATERMARK_STOCK_OPACITY,
  onSelectCorner = null,
}) => {
  // Firestore carries the source resolution once analysis lands. Until then —
  // and for any source whose doc never got one — the element reports its own
  // dimensions on loadedmetadata, which corrects the 16:9 fallback in place.
  const [probed, setProbed] = useState(null);
  const dims = resolution?.width > 0 && resolution?.height > 0 ? resolution : probed;
  const { aspect, widthPct, insetXPct, insetYPct } = markGeometry(dims?.width, dims?.height, sizeRatio);

  const markStyle = { width: `${widthPct}%`, opacity };
  markStyle[position.startsWith('top') ? 'top' : 'bottom'] = `${insetYPct}%`;
  markStyle[position.endsWith('left') ? 'left' : 'right'] = `${insetXPct}%`;

  return (
    <div
      className="op-watermark-preview"
      data-testid="watermark-preview"
      style={{ aspectRatio: aspect }}
    >
      {videoUrl ? (
        <video
          className="op-watermark-preview__frame"
          // The `#t=0.1` media fragment makes the browser seek to a real frame.
          // `preload="metadata"` alone paints nothing on iOS Safari until
          // playback starts, which would leave the mark floating on black and
          // defeat the whole point of showing it on the user's own footage.
          src={`${videoUrl}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden="true"
          onLoadedMetadata={(e) => setProbed({
            width: e.target.videoWidth,
            height: e.target.videoHeight,
          })}
        />
      ) : (
        <div
          className="op-watermark-preview__frame op-watermark-preview__frame--empty"
          data-testid="watermark-preview-placeholder"
        />
      )}
      {showMark && (
        <img
          src={logoUrl || watermarkAsset}
          alt=""
          aria-hidden="true"
          className="op-watermark-preview__mark"
          data-testid="watermark-preview-mark"
          style={markStyle}
        />
      )}
      {onSelectCorner && WATERMARK_POSITIONS.map((corner) => (
        <button
          key={corner}
          type="button"
          className={`op-watermark-preview__corner-hit op-watermark-preview__corner-hit--${corner}${corner === position ? ' is-selected' : ''}`}
          data-testid={`watermark-preview-corner-${corner}`}
          aria-label={`Move the watermark to the ${WATERMARK_POSITION_LABELS[corner].toLowerCase()} corner`}
          onClick={() => onSelectCorner(corner)}
        />
      ))}
    </div>
  );
};

// The tier whose pill sits on locked library tiles — the cheapest paid plan
// that ships watermark-free (and with it, watermark customization).
const WATERMARK_UNLOCK_TIER = PRICING_TIERS.find((t) => t.planId && t.watermarkFree);
const WATERMARK_UNLOCK_PILL = (WATERMARK_UNLOCK_TIER?.name || 'Solo').toUpperCase();

// Library order is SLOT order, not lexicographic: `logo0` leads whatever label
// it carries, so re-uploading into a freed slot never shuffles the row.
const bySlot = (a, b) =>
  WATERMARK_LOGO_SLOTS.indexOf(a.id) - WATERMARK_LOGO_SLOTS.indexOf(b.id);

// What a tile (and the picker button) CALLS a logo: the label the user gave it
// on upload, falling back to its slot number for an object that carries none.
// Module scope because the picker in the settings column and the tiles in the
// dialog have to name the same logo the same way.
const tileLabel = (logo) => logo.name || watermarkSlotLabel(logo.id);

const tileClass = (isSelected, extra = '') =>
  `op-watermark-library__tile${isSelected ? ' is-selected' : ''}${extra}`;

// THE FORMATS THE RENDER CAN ACTUALLY DECODE — not "any image".
// prepare_custom_logo() (packages/containers/generatevideo/processing/
// watermark.py) re-encodes every upload through plain Pillow, and the worker
// image installs Pillow alone: no SVG rasterizer, no pillow-heif, no
// pillow-avif. Those formats open fine in a browser but raise there, and the
// except branch returns None — which renders the STOCK Tweedia mark on a paid,
// credit-spending render with nothing on screen to explain it. So the picker
// refuses them here instead of previewing a logo the worker cannot open.
//
// One list feeds both gates: the check below and the file input's `accept`, so
// the OS picker greys the same files out rather than only the error doing it.
// storage.rules carries the same set as the server-side backstop.
const WATERMARK_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
// The same set in the words a person uses. Named in the error AND the
// pre-pick hint, so the rule is knowable before a file is chosen.
const WATERMARK_LOGO_FORMAT_WORDS = 'PNG, JPEG, WebP or GIF';

// How long a slider must sit still before its customization event fires. A
// drag emits `change` per pixel — a single sweep is ~25 events, which would
// bury the corner dots in the same funnel. 500ms matches SettingsEditor's
// debounce, the other slider-fed analytics in this app.
const WATERMARK_CUSTOMIZE_DEBOUNCE_MS = 500;

// THE LOGO LIBRARY, in a modal. The settings column shows a compact picker
// (current logo + "Change"); this is what that button opens — Tweedia first,
// then the account's uploads, then the + Upload tile, plus the upload contract
// and any error.
//
// PRESENTATIONAL ONLY. The storage listing, the selection, the upload/delete
// handlers and the dangling-id reconcile all stay on
// WatermarkCustomizationPanel above: the picker needs the listing for its own
// thumbnail whether or not this ever opens, and the reconcile has to run on
// panel mount rather than the first time somebody changes their logo.
//
// PORTALED TO <body>, and that is load-bearing: `.op-card__body--watermark`
// carries `container-type: inline-size` for the two-column split, which applies
// layout containment — and a layout-contained box is the containing block for
// its fixed-position descendants. Rendered in place, this overlay would be
// trapped inside the card instead of covering the viewport.
const WatermarkLogoDialog = ({
  logos,
  selectedLogoId,
  locked,
  uploading,
  slotsFull,
  uploadError,
  pendingDelete,
  fileInputRef,
  onSelect,
  onUploadClick,
  onFileChange,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  onClose,
}) => {
  const closeRef = useRef(null);
  const downOnBackdrop = useRef(false);

  // Focus moves INTO the dialog on open, so a keyboard user is not left tabbing
  // through the page behind an aria-modal. Close is the landing spot because it
  // is the one control every state of this dialog has.
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      // A locked account's tile click stacks the upgrade prompt ON TOP of this
      // dialog. Escape belongs to whatever is on top, so this one stands down
      // instead of both closing on one press.
      if (document.querySelector('.upgrade-prompt-overlay')) return;
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal(
    <div
      className="op-watermark-dialog-overlay"
      data-testid="watermark-logo-dialog-overlay"
      role="presentation"
      // Backdrop only: a click that started on the panel itself must not
      // dismiss the thing the user is working in. The press origin is tracked
      // because a drag that starts inside the panel and releases outside it
      // dispatches `click` on their common ancestor — this overlay — so the
      // release target alone would read as a backdrop click and close the
      // dialog out from under someone selecting a logo name.
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="op-watermark-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="watermark-logo-dialog-title"
        data-testid="watermark-logo-dialog"
      >
        <div className="op-watermark-dialog__head">
          <h3 className="op-watermark-dialog__title" id="watermark-logo-dialog-title">
            Choose a logo
          </h3>
          <button
            ref={closeRef}
            type="button"
            className="op-watermark-dialog__close"
            data-testid="watermark-logo-dialog-close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="op-watermark-dialog__body">
          <div
            className="op-watermark-library"
            role="group"
            aria-labelledby="watermark-logo-dialog-title"
            data-testid="watermark-logo-library"
          >
            <button
              type="button"
              className={tileClass(selectedLogoId === null)}
              data-testid="watermark-logo-tile-tweedia"
              aria-pressed={selectedLogoId === null}
              onClick={() => onSelect(null)}
            >
              {selectedLogoId === null && (
                <span className="op-watermark-library__check" aria-hidden="true">&#10003;</span>
              )}
              <span className="op-watermark-library__thumb-box">
                <img src={watermarkAsset} alt="" className="op-watermark-library__thumb" />
              </span>
              <span className="op-watermark-library__name">Tweedia</span>
            </button>
            {(logos || []).map((logo) => (
              // A wrapper, because the delete affordance is a second control and a
              // button cannot nest inside a button.
              <span className="op-watermark-library__slot" key={logo.id}>
                <button
                  type="button"
                  className={tileClass(selectedLogoId === logo.id, locked ? ' is-locked' : '')}
                  data-testid={`watermark-logo-tile-${logo.id}`}
                  aria-pressed={selectedLogoId === logo.id}
                  onClick={() => onSelect(logo)}
                >
                  {selectedLogoId === logo.id && (
                    <span className="op-watermark-library__check" aria-hidden="true">&#10003;</span>
                  )}
                  {locked && (
                    <span className="op-watermark-library__pill">{WATERMARK_UNLOCK_PILL}</span>
                  )}
                  <span className="op-watermark-library__thumb-box">
                    <img src={logo.url} alt="" className="op-watermark-library__thumb" />
                  </span>
                  <span className="op-watermark-library__name">{tileLabel(logo)}</span>
                </button>
                {!locked && (pendingDelete === logo.id ? (
                  // Two-step, because the object is gone for good and the slot is
                  // small enough to mis-tap.
                  <span className="op-watermark-library__confirm" data-testid={`watermark-logo-confirm-${logo.id}`}>
                    <button
                      type="button"
                      className="op-watermark-library__confirm-yes"
                      data-testid={`watermark-logo-delete-confirm-${logo.id}`}
                      onClick={() => onConfirmDelete(logo)}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      className="op-watermark-library__confirm-no"
                      data-testid={`watermark-logo-delete-cancel-${logo.id}`}
                      onClick={onCancelDelete}
                    >
                      Keep
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="op-watermark-library__delete"
                    data-testid={`watermark-logo-delete-${logo.id}`}
                    aria-label={`Remove ${tileLabel(logo)}`}
                    onClick={() => onRequestDelete(logo)}
                  >
                    &times;
                  </button>
                ))}
              </span>
            ))}
            <button
              type="button"
              className={`op-watermark-library__tile op-watermark-library__tile--upload${locked ? ' is-locked' : ''}`}
              data-testid="watermark-logo-upload"
              // Locked accounts keep a live tile: its whole job there is to open
              // the upsell. Only a full library (paid, five uploads) turns it off.
              disabled={uploading || (!locked && slotsFull)}
              onClick={onUploadClick}
            >
              {locked && (
                <span className="op-watermark-library__pill">{WATERMARK_UNLOCK_PILL}</span>
              )}
              <span className="op-watermark-library__upload-glyph" aria-hidden="true">+</span>
              <span className="op-watermark-library__name">
                {uploading ? 'Uploading…' : 'Upload'}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              // The SAME list the check in handleFileChange enforces, so the OS
              // picker greys out an SVG instead of the error being the first the
              // user hears of it. `accept` is a hint the user can defeat (Windows'
              // "All files"), which is why the check exists as well.
              accept={WATERMARK_LOGO_MIME_TYPES.join(',')}
              className="op-watermark-library__file"
              data-testid="watermark-logo-file-input"
              aria-label="Upload a watermark logo"
              onChange={onFileChange}
            />
          </div>
          {uploadError && (
            <p className="op-watermark-library__error" data-testid="watermark-upload-error">
              {uploadError}
            </p>
          )}
          {!locked && slotsFull && !uploadError && (
            // Says why the tile is off AND what to do about it — the card's rule
            // that a disabled control always names its way out.
            <p className="op-watermark-library__note" data-testid="watermark-slots-full">
              All 5 logo slots are full. Remove one to add another.
            </p>
          )}
          {!locked && !slotsFull && (
            <p className="op-watermark-library__note" data-testid="watermark-upload-hint">
              {WATERMARK_LOGO_FORMAT_WORDS} &mdash; a PNG with a transparent
              background works best. Up to 5 MB.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// The watermark card's open panel while the mark is ON: the logo picker (which
// opens the library dialog above), the corner/size/opacity controls stacked
// under it, and the live preview those feed. Locked accounts see the same panel
// with the paid affordances routed to the upsell — the server ignores their
// customization anyway, so their preview pins the stock mark at the stock
// geometry.
const WatermarkCustomizationPanel = ({
  projectId,
  videoUrl,
  resolution,
  draft,
  locked,
  onChange,
  onLockedClick,
}) => {
  const uid = auth?.currentUser?.uid || null;
  // null = listing hasn't resolved yet; [] = resolved, nothing uploaded.
  // Each entry: { id (the slot name), url, name (customMetadata.displayName
  // or '' when the object carries none) }.
  const [logos, setLogos] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  // The slot whose delete button was pressed, awaiting confirmation. Deleting
  // is destructive and un-undoable (the object is gone), and the tiles are
  // small enough to mis-tap, so the second press is the confirmation.
  const [pendingDelete, setPendingDelete] = useState(null);
  // The library lives in a modal now, so the settings column reads as one short
  // form instead of a tile row wrapping across it.
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!uid) {
      setLogos([]);
      return undefined;
    }
    let cancelled = false;
    listAll(storageFileRef(storage, `watermarks/${uid}`))
      .then((res) => Promise.all(
        // Objects outside the five slots cannot be a valid logo id — the API
        // would refuse one and the render would silently fall back to the
        // Tweedia mark — so they are not offered as choices. storage.rules
        // still allows deleting them, which is how such an object gets
        // cleared; nothing here can create one.
        res.items.filter((item) => isWatermarkLogoId(item.name)).map(async (item) => {
          // The human label is optional: an object uploaded before it existed
          // (or by anything but this card) falls back to its slot label.
          const [url, meta] = await Promise.all([
            getDownloadURL(item),
            getMetadata(item).catch(() => null),
          ]);
          return {
            id: item.name,
            url,
            name: (meta && meta.customMetadata && meta.customMetadata.displayName) || '',
          };
        })
      ))
      .then((items) => {
        if (!cancelled) setLogos(items.sort(bySlot));
      })
      .catch(() => {
        // A failed listing degrades to the stock-only row rather than wedging
        // the card — the render itself never depends on this list.
        if (!cancelled) setLogos([]);
      });
    return () => { cancelled = true; };
  }, [uid]);

  // The server honours customization only for entitled accounts; a locked
  // account's preview mirrors that by pinning the stock defaults. Normalised
  // once more on the way in so a draft that predates these fields (or carries
  // garbage) renders the defaults instead of NaN sliders.
  const selectedLogoId = locked || !isWatermarkLogoId(draft.watermarkLogoId)
    ? null
    : draft.watermarkLogoId;
  const position = locked ? WATERMARK_POSITION_DEFAULT : normalizeWatermarkPosition(draft.watermarkPosition);
  const size = locked ? WATERMARK_SIZE_DEFAULT : normalizeWatermarkSize(draft.watermarkSize);
  // Resolved against the SELECTED tile, so an untouched slider reads full
  // strength on the Tweedia chip and the toned-down default on an uploaded
  // logo — the two values the worker would pick for those same assets.
  const opacity = locked
    ? WATERMARK_STOCK_OPACITY
    : normalizeWatermarkOpacity(draft.watermarkOpacity, selectedLogoId);
  const selectedLogo = selectedLogoId && Array.isArray(logos)
    ? logos.find((l) => l.id === selectedLogoId)
    : null;

  // A DANGLING ID IS A PROMISE THE RENDER CANNOT KEEP, so the draft is
  // reconciled the moment the library says the slot is empty.
  //
  // This is reachable without any bug: `handleDelete` clears the id from the
  // CURRENT draft only, so the next project seeded from the account defaults
  // (users/{uid}.watermarkDefaults.logoId) opens pointing at a slot that is
  // gone. The preview already falls back to the stock mark, but nothing else
  // did — the hint still said "Your logo renders in the … corner", the render
  // receipt still named it, and no tile showed as pressed. The render itself
  // falls back to the Tweedia mark, so the draft is corrected to say so.
  //
  // `Array.isArray(logos)` IS THE LOAD GATE: null means the listing is still
  // in flight, and clearing then would wipe a perfectly valid selection on
  // every mount. Locked accounts never reach this — `selectedLogoId` above is
  // pinned to null while `locked`, so the effect returns on its second guard.
  //
  // The ref makes the clear at-most-once per id: `onChange` is a fresh closure
  // on every parent render, so the effect re-runs constantly, and an owner who
  // did not apply the change (or applied it late) would otherwise be told
  // again and again in a loop.
  const reconciledLogoIdRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(logos) || !selectedLogoId || selectedLogo) return;
    if (reconciledLogoIdRef.current === selectedLogoId) return;
    reconciledLogoIdRef.current = selectedLogoId;
    onChange({ watermarkLogoId: null, watermarkLogoName: null });
  }, [logos, selectedLogoId, selectedLogo, onChange]);

  // The five slots are the whole library — storage.rules will not create a
  // sixth — so a full library disables the upload tile rather than failing the
  // write, and names deleting one as the way forward.
  const freeSlot = firstFreeWatermarkSlot((logos || []).map((l) => l.id));
  const slotsFull = Array.isArray(logos) && freeSlot === null;

  // Closing resets the dialog's own transient state, so it never reopens
  // mid-confirmation or still showing the error from a pick two minutes ago.
  const closeLogoDialog = useCallback(() => {
    setLogoDialogOpen(false);
    setPendingDelete(null);
    setUploadError(null);
  }, []);

  // Focus goes back to the button that opened the dialog — otherwise it lands
  // on <body> and a keyboard user restarts the page.
  const dialogWasOpenRef = useRef(false);
  useEffect(() => {
    if (dialogWasOpenRef.current && !logoDialogOpen && pickerRef.current) {
      pickerRef.current.focus();
    }
    dialogWasOpenRef.current = logoDialogOpen;
  }, [logoDialogOpen]);

  const selectLogo = (logo) => {
    if (locked) {
      // Every tile but Tweedia is the upsell; the dialog stays put behind the
      // prompt so a "no thanks" lands back on the choice.
      if (logo !== null) {
        onLockedClick();
        return;
      }
      // Tweedia is the one choice a locked account already has, so clicking it
      // is a confirmation — the dialog gets out of the way.
      closeLogoDialog();
      return;
    }
    // A pick is the whole reason the dialog is open, so it closes on the way
    // out. (Deleting does NOT — the user may be clearing several slots.)
    closeLogoDialog();
    onChange({
      watermarkLogoId: logo === null ? null : logo.id,
      // Display only — the receipt in the render dialog names the logo the
      // user actually recognises rather than the slot it happens to occupy.
      watermarkLogoName: logo === null ? null : (logo.name || ''),
    });
    trackEvent('watermark_logo_selected', {
      project_id: projectId,
      logo_id: logo === null ? 'tweedia' : logo.id,
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Cleared so picking the same file again re-fires change.
    event.target.value = '';
    if (!file) return;
    setUploadError(null);
    // An allowlist, not an `image/` prefix: an SVG or HEIC is an image the
    // browser previews perfectly and the worker's Pillow cannot open — see
    // WATERMARK_LOGO_MIME_TYPES. The message names the formats rather than
    // saying "not an image", because for those files that would be a lie.
    if (!file.type || !WATERMARK_LOGO_MIME_TYPES.includes(file.type)) {
      setUploadError(
        `That file type can't be used as a logo. Upload a ${WATERMARK_LOGO_FORMAT_WORDS} — a PNG with transparency works best.`
      );
      return;
    }
    if (file.size > WATERMARK_LOGO_MAX_BYTES) {
      setUploadError('That image is over 5 MB. Export a smaller version and try again.');
      return;
    }
    // Re-checked here, not just on the disabled tile: the listing could have
    // resolved between the click and the pick, and an upload to a taken slot
    // would silently overwrite a logo the user still has selected elsewhere.
    const slot = firstFreeWatermarkSlot((logos || []).map((l) => l.id));
    if (!slot) {
      setUploadError('All 5 logo slots are full. Remove one to add another.');
      return;
    }
    // The id is the SLOT; this is only what the tile and the receipt show.
    const label = watermarkLogoLabel(file.name);
    setUploading(true);
    try {
      const dest = storageFileRef(storage, `watermarks/${uid}/${slot}`);
      await uploadBytes(dest, file, {
        contentType: file.type,
        customMetadata: { displayName: label },
      });
      const url = await getDownloadURL(dest);
      setLogos((prev) => [
        ...(prev || []).filter((l) => l.id !== slot),
        { id: slot, url, name: label },
      ].sort(bySlot));
      onChange({ watermarkLogoId: slot, watermarkLogoName: label });
      trackEvent('watermark_logo_uploaded', { project_id: projectId, logo_id: slot });
      // A successful upload auto-selects the new logo, and a selection closes
      // the dialog — same as tapping a tile, because that is what just
      // happened. A FAILED upload keeps it open, showing the error.
      closeLogoDialog();
    } catch (e) {
      setUploadError('Upload failed. Check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (logo) => {
    setUploadError(null);
    setPendingDelete(null);
    try {
      await deleteObject(storageFileRef(storage, `watermarks/${uid}/${logo.id}`));
    } catch (e) {
      setUploadError('Could not remove that logo. Check your connection and try again.');
      return;
    }
    setLogos((prev) => (prev || []).filter((l) => l.id !== logo.id));
    // The selection cannot outlive the object: a render pointed at a deleted
    // slot falls back to the Tweedia mark server-side, so the card says so
    // now instead of letting the receipt promise a logo that is gone.
    if (selectedLogoId === logo.id) {
      // Recorded as already reconciled, so the effect above does not report the
      // same clear a second time when the tile leaves the list.
      reconciledLogoIdRef.current = logo.id;
      onChange({ watermarkLogoId: null, watermarkLogoName: null });
    }
    trackEvent('watermark_logo_deleted', { project_id: projectId, logo_id: logo.id });
  };

  // What the picker button SHOWS without opening anything. Three cases, and the
  // third is why the listing lives on this panel and not in the dialog: while
  // `logos` is still null a saved custom logo has no url and no stored label
  // yet, so the button falls back to the draft's own display name (or the slot
  // number) and a neutral thumb rather than flashing "Tweedia" at somebody
  // whose logo is about to appear.
  const pickerName = selectedLogoId === null
    ? 'Tweedia'
    : (selectedLogo ? tileLabel(selectedLogo) : (draft.watermarkLogoName || watermarkSlotLabel(selectedLogoId)));
  const pickerThumbUrl = selectedLogoId === null
    ? watermarkAsset
    : (selectedLogo ? selectedLogo.url : null);

  // Every corner/size/opacity change goes through here, so the customization
  // funnel has no hole where the drop-off is: the people who open the panel,
  // move the mark around and never render are exactly who that number is for.
  // The logo tiles already report themselves (watermark_logo_selected /
  // _uploaded / _deleted); this is the same for the three geometry controls.
  //
  // ONE event with the key that moved, not three event names — the controls
  // stay comparable in one report, and a fourth knob needs no new event.
  //
  // Per-KEY debounce timers, not one shared timer: a shared one would let an
  // opacity drag swallow the size drag that came just before it, and the
  // report would then under-count whichever control was adjusted first.
  const customizeTimersRef = useRef({});
  useEffect(() => {
    const timers = customizeTimersRef.current;
    return () => { Object.keys(timers).forEach((k) => clearTimeout(timers[k])); };
  }, []);

  const changeSetting = (key, changes, { debounce = false } = {}) => {
    // Second guard behind the controls' own `disabled`, the same posture the
    // watermark switch takes: React dispatches change on a disabled input from
    // any programmatic set, and the server ignores a locked account's
    // customization anyway — so an inert control stays inert in the handler
    // too, for the draft AND for the funnel.
    if (locked) return;
    onChange(changes);
    const fire = () => trackEvent('watermark_customized', {
      project_id: projectId,
      setting: key,
    });
    if (!debounce) {
      fire();
      return;
    }
    const timers = customizeTimersRef.current;
    if (timers[key]) clearTimeout(timers[key]);
    timers[key] = setTimeout(() => {
      delete timers[key];
      fire();
    }, WATERMARK_CUSTOMIZE_DEBOUNCE_MS);
  };

  return (
    // Two columns once the card is wide enough: the preview reads LEFT and the
    // adjustments RIGHT (the row-reverse in OperationCards.css does the swap).
    // DOM order stays settings-then-preview, so a narrow card keeps today's
    // stacking and the tab order still runs the controls, then the preview.
    <div className="op-watermark-layout">
      <div
        className="op-watermark-layout__settings"
        data-testid="watermark-settings-column"
      >
        {/* One vertical form: logo, then corner, then the two sliders. Each
            row is label-above-control and left-aligned, so the column reads
            top to bottom instead of as a wrapping strip of widgets. */}
        <div className="op-watermark-controls" data-testid="watermark-controls">
          <div className="op-watermark-controls__group">
            <span className="op-card__field-label" id="watermark-logo-label">Logo</span>
            {/* The whole logo row: what is selected, and the way to change it.
                The library itself moved into a dialog — five tiles and their
                delete affordances wrapped across this column and pushed the
                corner/size/opacity controls off the bottom of the card. */}
            <button
              ref={pickerRef}
              type="button"
              id="watermark-logo-picker-button"
              className="op-watermark-picker"
              data-testid="watermark-logo-picker"
              aria-haspopup="dialog"
              aria-expanded={logoDialogOpen}
              // Label + value, the way a settings row reads aloud: "Logo,
              // Tweedia, Change".
              aria-labelledby="watermark-logo-label watermark-logo-picker-button"
              onClick={() => setLogoDialogOpen(true)}
            >
              <span className="op-watermark-picker__thumb-box">
                {pickerThumbUrl && (
                  <img src={pickerThumbUrl} alt="" className="op-watermark-picker__thumb" />
                )}
              </span>
              <span className="op-watermark-picker__name">{pickerName}</span>
              <span className="op-watermark-picker__change">
                Change
                <span className="op-watermark-picker__chevron" aria-hidden="true">&rsaquo;</span>
              </span>
            </button>
          </div>
          <div className="op-watermark-controls__group">
            <label
              className="op-card__field-label"
              id="watermark-corner-label"
              htmlFor="watermark-corner-select"
            >
              Corner
            </label>
            {/* A full-width dropdown, not the old 2x2 dot grid: the dots were a
                thumbnail-sized widget in a column of full-width controls, so
                they left dead space to their right and broke the form's edge. */}
            <div className="op-watermark-corner-wrap">
              <select
                id="watermark-corner-select"
                className="op-watermark-corner-select"
                data-testid="watermark-corner-picker"
                value={position}
                disabled={locked}
                onChange={(e) => changeSetting('position', { watermarkPosition: e.target.value })}
              >
                {WATERMARK_CORNER_DISPLAY_ORDER.map((corner) => (
                  <option key={corner} value={corner}>
                    {WATERMARK_POSITION_LABELS[corner]}
                  </option>
                ))}
              </select>
              <span className="op-watermark-corner-chevron" aria-hidden="true">&#9662;</span>
            </div>
          </div>
          <label className="op-watermark-controls__slider">
            <span className="op-card__field-label">
              Size &mdash; {Math.round(size * 100)}% of frame width
            </span>
            <input
              type="range"
              min={WATERMARK_SIZE_MIN * 100}
              max={WATERMARK_SIZE_MAX * 100}
              step={1}
              value={Math.round(size * 100)}
              disabled={locked}
              data-testid="watermark-size-slider"
              aria-label="Watermark size, percent of frame width"
              onChange={(e) => changeSetting(
                'size',
                { watermarkSize: Number(e.target.value) / 100 },
                { debounce: true }
              )}
            />
          </label>
          <label className="op-watermark-controls__slider">
            <span className="op-card__field-label">
              Opacity &mdash; {Math.round(opacity * 100)}%
            </span>
            <input
              type="range"
              min={WATERMARK_OPACITY_MIN * 100}
              max={WATERMARK_OPACITY_MAX * 100}
              step={1}
              value={Math.round(opacity * 100)}
              disabled={locked}
              data-testid="watermark-opacity-slider"
              aria-label="Watermark opacity, percent"
              onChange={(e) => changeSetting(
                'opacity',
                { watermarkOpacity: Number(e.target.value) / 100 },
                { debounce: true }
              )}
            />
          </label>
        </div>
      </div>
      <div
        className="op-watermark-layout__preview"
        data-testid="watermark-preview-column"
      >
        <WatermarkPreview
          videoUrl={videoUrl}
          resolution={resolution}
          showMark
          logoUrl={selectedLogo ? selectedLogo.url : null}
          position={position}
          sizeRatio={size}
          opacity={opacity}
          onSelectCorner={locked
            ? null
            : (corner) => changeSetting('position', { watermarkPosition: corner })}
        />
        <p
          className="op-watermark-preview__caption"
          data-testid="watermark-preview-caption"
        >
          Preview &mdash; a frame from your own footage.
        </p>
      </div>
      {/* Rendered last, but it portals to <body> — see WatermarkLogoDialog.
          Every piece of state it shows still belongs to this panel. */}
      {logoDialogOpen && (
        <WatermarkLogoDialog
          logos={logos}
          selectedLogoId={selectedLogoId}
          locked={locked}
          uploading={uploading}
          slotsFull={slotsFull}
          uploadError={uploadError}
          pendingDelete={pendingDelete}
          fileInputRef={fileInputRef}
          onSelect={selectLogo}
          onUploadClick={() => {
            if (locked) {
              onLockedClick();
              return;
            }
            if (fileInputRef.current) fileInputRef.current.click();
          }}
          onFileChange={handleFileChange}
          onRequestDelete={(logo) => setPendingDelete(logo.id)}
          // Deleting keeps the dialog OPEN — clearing a slot is library
          // management, not a choice of logo.
          onConfirmDelete={handleDelete}
          onCancelDelete={() => setPendingDelete(null)}
          onClose={closeLogoDialog}
        />
      )}
    </div>
  );
};

const OperationCards = ({
  draft,
  onDraftChange,
  video,
  fps,
  entitlements,
  projectId,
  // The source video's playable URL, used only as the still frame behind the
  // watermark preview. Absent (still uploading, or a photo project mid-compile)
  // simply draws a dark placeholder.
  videoUrl,
  // The motion tuning UI (threshold sliders + timeline). Passed in rather than
  // imported so this module stays free of the heavy editor tree, and so the
  // trim card can decide whether it is allowed on screen at all.
  motionControls,
  // #532 — the `motionPace` feature flag. While it is dark the Motion card is
  // the pre-#532 "Trim stillness" card, byte for byte: no mode toggle, no
  // renaming, no new writes. MotionPace resolves to off in the worker anyway
  // (registry: featureFlag `motionPace`, default false), so offering the choice
  // would be offering an edit that will not happen.
  motionPaceEnabled = false,
  // Persists the motion keys so the server recomputes the speed curve. Only the
  // Motion section calls it, and only for changes MotionPace is part of.
  onMotionPersist,
  // True while that write is in flight — the pace chart wears the same
  // "Recalculating…" overlay the trim timeline does.
  isRecalculating = false,
  // The editor's <video> (the SOURCE footage). Passed straight through to the
  // Motion section so the pace chart can seek it and draw a playhead on it,
  // exactly as the trim timeline inside `motionControls` already does. Absent
  // outside the editor, which simply leaves that chart inert.
  videoPlayerRef,
}) => {
  const [selected, setSelected] = useState(null);
  const [upsellPrompt, setUpsellPrompt] = useState(null);
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
  const presets = useMemo(() => getTransitionPresets(safeFps), [safeFps]);
  const [customSelected, setCustomSelected] = useState(false);
  const [customDraft, setCustomDraft] = useState(undefined);
  const cardRefs = useRef({});
  const refFor = (operationId) => (el) => { cardRefs.current[operationId] = el; };

  const select = (operationId) => {
    setSelected((prev) => {
      const next = prev === operationId ? null : operationId;
      if (next) trackEvent('operation_card_expanded', { project_id: projectId, operation_id: next });
      return next;
    });
  };

  // The cards are an exclusive accordion, so expanding a lower one collapses a
  // taller one above it — the page shrinks under the scroll offset and the card
  // the user just opened ends up off-screen below the fold. 'nearest' is a
  // no-op while the card is already fully visible, so this only ever corrects
  // that collapse; it never hijacks a scroll.
  useEffect(() => {
    if (!selected) return;
    const el = cardRefs.current[selected];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  const patch = (operationId, changes) => {
    onDraftChange(changes);
    trackEvent('operation_toggled', {
      project_id: projectId,
      operation_id: operationId,
      ...changes,
    });
  };

  // --- trim_stillness ------------------------------------------------------
  const segments = video?.motionSegments;
  const analysisLanded = Array.isArray(segments);
  // Whether analysis can still write segments. While it is in flight the render
  // itself is refused server-side (SOURCE_NOT_READY), so this card must not
  // offer "switch me off to render the full length now" — see renderBlockReason.
  const analysisRunning = isAnalysisInFlight(video);
  // --- the Motion section (#532) -------------------------------------------
  // WHICH MODE IS ARMED IS RESOLVED, NOT READ. `motionModeOf` runs the same
  // `resolveOperations` the render does — feature-flag gate first, exclusive
  // group after — so the toggle can never show a mode the render would not pick.
  // With the flag dark it collapses to exactly `draft.trimStillness`, which is
  // what keeps this card unchanged until MotionPace is lit.
  const motionMode = motionModeOf(draft, motionPaceEnabled);
  const paceMode = motionMode === MOTION_MODE_PACE;
  const trimOn = motionMode !== null;
  // Which mode the switch restores when it is turned back on. Remembered rather
  // than defaulted, so flicking the section off and on does not silently move a
  // user off MotionPace — and it is UI memory only, never persisted.
  const lastModeRef = useRef(motionMode || MOTION_MODE_TRIM);
  if (motionMode) lastModeRef.current = motionMode;

  // THE MOTION MERGE LIVES HERE, NOT IN THE SECTION. Callers hand up only the
  // keys they changed; this folds them into the draft as of the LATEST render.
  //
  // Why the parent: MotionSection's debounced slider commits can fire from a
  // section that has already unmounted, and the master switch below is one of
  // those unmount paths. The section never re-renders on that path, so a draft
  // snapshot it held would still say MotionPace is on — flushing it wrote
  // `variableSpeed: true` after the switch had just written false, and the
  // draft is hydrated straight back from those settings, so Motion came back on
  // after a reload. This card outlives the section and so always has the draft
  // the switch just produced.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const persistMotion = (changes) => {
    if (onMotionPersist) onMotionPersist({ ...draftRef.current, ...changes });
  };

  const motionName = motionPaceEnabled
    ? MOTION_SECTION.displayName
    : displayNameOf('trim_stillness');

  // MotionPace needs motion DATA but not motion SEGMENTS, so the two
  // analysis-shaped warnings below belong to trim mode alone: a zero-segment
  // project is a perfectly good MotionPace edit.
  const trimState = !trimOn
    ? 'Off'
    : paceMode
      ? MOTION_MODES.pace.badge
      : !analysisLanded
        ? 'Waiting on analysis'
        : segments.length === 0
          ? 'No motion found'
          : (motionPaceEnabled ? MOTION_MODES.trim.badge : 'On');
  const trimTone = !trimOn
    ? 'off'
    : (!paceMode && (!analysisLanded || segments.length === 0)) ? 'warn' : 'on';

  // The switch state, expressed as the mode change it makes. Turning the section
  // off writes BOTH operations off; turning it on restores the remembered mode.
  const setMotionEnabled = (checked) => {
    const next = checked ? lastModeRef.current : null;
    const changes = motionModeChanges(next);
    patch(
      next === MOTION_MODE_PACE || (!checked && paceMode) ? 'variable_speed' : 'trim_stillness',
      changes,
    );
    // Same rule the mode toggle follows: only a change MotionPace is part of
    // needs a settings write (to compute a curve, or to clear one).
    if (next === MOTION_MODE_PACE || paceMode) {
      persistMotion(changes);
    }
  };

  // MotionTrim's half of the shared chart slot: the tuning panel and the
  // kept-segment timeline, or the reason there is nothing to tune. Lifted out of
  // the card below UNCHANGED — this is the branch #532 must not disturb, and the
  // Motion section renders it verbatim when trim mode is selected.
  const trimPanel = !analysisLanded ? (
    <OffReason operationId="trim_stillness">
      {analysisRunning
        ? 'Motion analysis is still running. You can tune this once it finishes.'
        : "Motion analysis didn't finish, so there's nothing to trim. Switch this off to render the full length."}
    </OffReason>
  ) : segments.length === 0 ? (
    <div>
      <OffReason operationId="trim_stillness">
        No motion found at this sensitivity. Lower the threshold below, or
        switch this off to keep the full length.
      </OffReason>
      {motionControls}
    </div>
  ) : motionControls;

  // --- deflicker -----------------------------------------------------------
  // #402 writes video.flickerAnalysis. It does not exist yet, and a project
  // analyzed before it ships never will have it — so every read is defensive
  // and an absent or malformed result simply means "not detected". This card
  // must never gate on it or throw.
  const flicker = video?.flickerAnalysis;
  const flickerDetected = !!(flicker && typeof flicker === 'object' && flicker.detected === true);
  const flickerMagnitude = flicker && Number.isFinite(flicker.magnitudePercent)
    ? flicker.magnitudePercent
    : null;
  const deflickerOn = !!draft.deflicker;
  const deflickerState = deflickerOn ? (flickerDetected ? 'Detected' : 'On') : 'Off';
  // The stored luminance summary (#402) that the proof graph plots (#405).
  // Read defensively for the same reason flickerAnalysis is: a project analyzed
  // before #402 has neither, and the card must render regardless.
  const luminanceTrack = video?.luminanceTrack;

  // How hard the fix smooths, in source frames, persisted on the draft as
  // `deflickerWindow`. Normalised rather than read raw: every draft written
  // before the Strength control existed has no such field, and those edits must
  // keep rendering with the window they always have.
  //
  // The control is SEPARATE from the switch on purpose. It never turns the
  // operation on or off — a user who picks Strong on a card they left off has
  // chosen how it will smooth if they switch it on, not switched it on.
  const deflickerWindow = normalizeDeflickerWindow(draft.deflickerWindow);

  // The headline before/after numbers, on the same scale as the "Flicker
  // detected (about 2.4% frame-to-frame)" line above them. `magnitudePercent`
  // is the measurement; `magnitudePercentAfter` is a map of window-as-string ->
  // what that correction is estimated to leave.
  //
  // Both halves are read defensively and INDEPENDENTLY of each other, because
  // three different vintages of project exist at once: analyzed before #402 (no
  // flickerAnalysis at all), analyzed before the Strength control (a magnitude
  // but no after-map), and current. A missing number prints nothing — never
  // "undefined%", and never a 0 that would read as "this fixes it completely".
  const afterByWindow = flicker
    && flicker.magnitudePercentAfter
    && typeof flicker.magnitudePercentAfter === 'object'
    && !Array.isArray(flicker.magnitudePercentAfter)
    ? flicker.magnitudePercentAfter
    : null;
  const rawAfter = afterByWindow ? afterByWindow[String(deflickerWindow)] : undefined;
  const flickerMagnitudeAfter = Number.isFinite(rawAfter) ? rawAfter : null;
  // Both numbers or neither: "3.4% → " is worse than no read-out, and an
  // after-number with nothing to compare it to is a figure with no meaning.
  const showFlickerReadout = flickerMagnitude != null && flickerMagnitudeAfter != null;

  // --- transitions ---------------------------------------------------------
  //
  // ONE CARD, ONE CONCEPT. A single crossfade length
  // (`transition_length_frames`) is what the worker applies at every boundary
  // it fades; capture gaps and clip joins are not operations of their own but
  // PLACES that length lands, so they are rows in this card's checklist rather
  // than sibling cards whose switches did nothing until this one was on.
  const transitionsOn = transitionsEnabled(draft);
  // The boundary declarations, read exactly as the worker resolves them.
  //
  // Both fields are owner-writable in Firestore, so both go through
  // `normalizeGapFrames` — the same coercion the fade rule applies. Counting
  // the raw arrays would offer a row whose checkbox then does nothing, because
  // the rule it drives had already dropped every value (a document holding
  // `["500"]` reads as one join, and fades at none).
  const captureGaps = normalizeGapFrames(video?.captureGapFrames);
  const clipJoins = normalizeGapFrames(video?.concatJoinFrames);
  const hasCaptureGaps = captureGaps.length > 0;
  const hasClipJoins = clipJoins.length > 0;
  const captureGapTransitionsOn = !!draft.captureGapTransitions;
  const clipJoinTransitionsOn = !!draft.clipJoinTransitions;
  // Mirrors `effectiveFadeFrames`'s `joinsApply`: when clip joins are on AND
  // this project actually has joins, the worker fades the UNION of gap and
  // join frames — so capture gaps fade even with their own switch off.
  //
  // That fact is carried by the row's META LINE, not by force-checking its box.
  // The box keeps showing — and writing — the user's OWN gap choice, which is
  // the only way that choice can be made while joins cover it and still mean
  // something when they are turned back off. Force-checking it instead made the
  // row un-editable (no way to record `captureGapTransitions: true` at all) and
  // silently dropped the gap fades the moment Clip joins were unticked: the box
  // flipped itself from ticked to unticked and those gaps went back to hard
  // cuts. Same split the pre-consolidation card made with its "Included" badge
  // — live switch, coverage stated alongside it.
  const joinsApply = clipJoinTransitionsOn && hasClipJoins;
  const captureGapsCovered = joinsApply && !captureGapTransitionsOn;

  // The list the render will actually cut, and the fade frames it will honour —
  // one read, shared by the clip count and the checklist below so they cannot
  // disagree about the same edit.
  //
  // Split-aware: capture-gap crossfades cut a one-segment edit into several, so
  // counting the analyzed segments here would contradict the render dialog.
  const renderSegments = renderSegmentsForDraft(video, draft);
  const clips = Array.isArray(renderSegments) ? renderSegments.length : null;
  const singleClip = clips === 1;
  const { frames: fadeFrames, enabled: fadeEnabled } = effectiveFadeFrames(video, draft);
  // Boundaries that fade with BOTH switches off — the ordinary motion cuts.
  // `renderCrossfadeCount(..., false, ...)` is the worker's own rule: every
  // boundary that is not a gap/join frame fades whenever the length is > 0, and
  // there is no switch for them. That is what the inert first row reports.
  const motionCutCount = renderCrossfadeCount(renderSegments, fadeFrames, false, draft.transitionFrames);
  // How many of the declared boundaries the render will ACTUALLY dissolve
  // (#589). The pipeline drops a fade whose neighbouring pieces are too short to
  // absorb the overlap — correct behaviour, but the rows used to advertise the
  // boundaries DETECTED, so a card could promise "3 gaps" of dissolves when
  // every one would be skipped. Computed against the union the render honours,
  // so the MAX_CAPTURE_GAP_SPLITS cap lands the same way it does in the worker.
  const gapTally = fadeBoundaryTally(renderSegments, fadeFrames, draft.transitionFrames, captureGaps);
  const joinTally = fadeBoundaryTally(renderSegments, fadeFrames, draft.transitionFrames, clipJoins);
  // Every crossfade this render will apply, of any kind. Zero with more than one
  // clip means the length is set and the card looks armed, but not one dissolve
  // survives — so the badge has to say so rather than name a preset.
  const renderedCrossfades = renderCrossfadeCount(
    renderSegments, fadeFrames, fadeEnabled, draft.transitionFrames,
  );

  // "only 2 of the 3 that reach the render will fade", with the one-line why.
  // Returns null for the ordinary case where every boundary that reaches the
  // render dissolves, so the unchanged copy stays literally unchanged.
  //
  // WHY THE DENOMINATOR IS SPELLED OUT. The gap row's first clause counts gaps
  // DETECTED; a shortfall is always out of `tally.boundaries`, the boundaries
  // that actually REACH the render, which is deliberately smaller — a gap
  // sitting inside trimmed stillness never becomes a boundary and is not a loss.
  // A bare "only 1 will fade" beside "5 gaps detected" invites the reader to
  // subtract and conclude 4 clips were too short when the true answer is 2.
  //
  // WHY THE REASON IS PICKED, NOT FIXED. The render drops a fade for two
  // unrelated reasons and the card has to name the right one. A 31-day shoot
  // with 100-frame days has 30 long-enough boundaries and loses half of them to
  // the MAX_CAPTURE_GAP_SPLITS ceiling — "too short to overlap" is simply false
  // there, and multi-week construction is the core use case.
  //
  // WHY THE CAP IS DESCRIBED AS A SPREAD, NOT AS A CEILING ON THIS ROW. Two
  // things make a bare "at most 24 of these boundaries" untrue beside the number
  // the row just printed:
  //
  //   1. The cap is a STRIDE, not a truncation. `editDraft.js` keeps every
  //      stride-th qualifying boundary (`stride = ceil(n / 24)`) so the fades
  //      stay spread across the shoot, which lands BELOW 24 for any n that is
  //      not a multiple of the stride — 30 boundaries give stride 2 and 15
  //      fades, never 24. "only 15 of the 30 — at most 24" is arithmetic that
  //      does not close, the same trap the denominator wording above closes.
  //   2. The 24 counts the UNION of camera pauses and clip joins (the stride
  //      runs over `fadeFrames`), while `tally.boundaries` counts only this
  //      row's own frames. A 25-pause + 1-join project renders a joins row of
  //      one boundary — quoting a ceiling of 24 at it is nonsense.
  //
  // So the copy names the MECHANISM and scopes the number with "in total",
  // which is true whatever `tally.fading` came out to.
  const spreadReason = `one render fades at most ${MAX_CAPTURE_GAP_SPLITS} camera pauses and clip joins in total, so it spreads them evenly through the video`;
  const fadeShortfall = (tally, on) => {
    if (!on || tally.boundaries === 0 || tally.fading === tally.boundaries) return null;
    // Singular gets its own sentence: "none of the 1 that reach the render" is
    // not English, and a one-boundary row is the common shape on the joins side.
    const head = tally.boundaries === 1
      ? 'the one boundary that reaches the render will not fade'
      : tally.fading === 0
        ? `none of the ${tally.boundaries} that reach the render will fade`
        : `only ${tally.fading} of the ${tally.boundaries} that reach the render will fade`;
    let why;
    if (tally.droppedShort === 0) {
      why = spreadReason;
    } else if (tally.droppedCapped === 0) {
      why = tally.fading === 0
        ? 'the clips either side are too short to overlap'
        : 'the rest sit between clips too short to overlap';
    } else {
      // Both causes fired, so both are named — "the rest" would be wrong for
      // either one alone.
      why = `some sit between clips too short to overlap, and ${spreadReason}`;
    }
    return `${head} — ${why}`;
  };
  const presetId = matchTransitionPreset(draft.transitionFrames, safeFps);
  // The Custom tile is selected either because the user picked it, or because
  // the saved length matches no preset — a value only the custom input could
  // have produced, so it must come back selected on reload.
  const customActive = customSelected || presetId === 'custom';
  const presetLabel = presets.find((p) => p.id === presetId)?.label
    || `${formatSeconds(framesToSeconds(draft.transitionFrames, safeFps))}s fade`;
  // "Not applied" covers two ways a card can look armed and render nothing: one
  // clip has no boundaries at all, and — since #589 — a multi-clip edit whose
  // every boundary is dropped or suppressed. Naming the preset in either case
  // promises dissolves the render will not contain.
  const transitionsState = !transitionsOn
    ? 'Off'
    : (singleClip || renderedCrossfades === 0)
      ? 'Not applied'
      : presetLabel;

  const selectPreset = (preset) => {
    setCustomDraft(undefined);
    setCustomSelected(false);
    patch('transitions', { transitionFrames: preset.frames });
  };

  const handleCustomChange = (e) => {
    const raw = e.target.value;
    setCustomDraft(raw);
    if (raw === '' || raw === '-' || raw === '.') return;
    const seconds = parseFloat(raw);
    if (Number.isNaN(seconds)) return;
    onDraftChange({ transitionFrames: secondsToFrames(clamp(seconds, 0, 3), safeFps) });
  };

  const handleCustomBlur = () => {
    setCustomDraft(undefined);
    const current = framesToSeconds(draft.transitionFrames, safeFps);
    const clamped = clamp(current, 0, 3);
    if (clamped !== current) {
      onDraftChange({ transitionFrames: secondsToFrames(clamped, safeFps) });
    }
  };

  // Turning transitions back on restores the default crossfade rather than
  // leaving the switch on with 0 frames, which would render as a cut and make
  // the badge lie.
  const defaultTransitionFrames = presets.find((p) => p.id === 'quick')?.frames
    || Math.round(safeFps * 0.5);

  // --- the "Fade at" checklist ----------------------------------------------
  // Only the boundary types this project actually HAS get a row: an ordinary
  // single-file upload sees neither camera pauses nor clip joins, and a paced
  // edit has no motion cuts to fade. A row for a boundary that does not exist
  // is a control that renders the same video either way.
  //
  // The motion-cut row is inert on purpose — see FadeRow. It is suppressed on a
  // paced edit (MotionPace cuts nowhere) and whenever the count is zero, which
  // covers a compile whose only boundaries came from gap splits.
  const showMotionCuts = motionMode === MOTION_MODE_TRIM && !singleClip && motionCutCount > 0;
  const showFadeChecklist = showMotionCuts || hasCaptureGaps || hasClipJoins;
  // The pace explainer takes the motion-cut row's place in the list. When there
  // is no list at all (no gaps, no joins — the usual paced project, which is
  // therefore a single clip) the inline note above the presets carries the same
  // sentence instead, so it always appears exactly once.
  const paceNoteInChecklist = paceMode && showFadeChecklist;

  // Named counts, shared by the checklist rows and by the OFF copy — which is
  // where the dependency lives now that the two sibling switches are gone: a
  // user who turns Transitions off is told what stops fading, rather than left
  // looking at two live toggles that do nothing.
  const captureGapWord = `${captureGaps.length} camera ${captureGaps.length === 1 ? 'pause' : 'pauses'}`;
  const clipJoinWord = `${clipJoins.length} clip ${clipJoins.length === 1 ? 'join' : 'joins'}`;
  // ONLY the boundaries turning the card back on would actually fade. Each entry
  // is gated on its own switch, not merely on the boundary existing: a project
  // whose Clip joins box is unticked would otherwise read "including your 3 clip
  // joins", and turning Transitions on would still hard-cut every one of them.
  // Capture gaps count when their own switch is on OR when joins cover them —
  // the same `joinsApply` union the checklist states.
  const offBoundaries = [
    ...(joinsApply ? [clipJoinWord] : []),
    ...(hasCaptureGaps && (captureGapTransitionsOn || joinsApply) ? [captureGapWord] : []),
  ];

  // --- watermark_removal ---------------------------------------------------
  // Same three-state posture as the render dialog had (#344/#350): pending
  // while the users/{uid} snapshot is in flight, locked once we know the
  // account is Free, actionable otherwise. Never "locked" during the pending
  // window — that would pitch an upgrade at someone who already pays for one.
  const watermarkPending = !!(entitlements && entitlements.loading);
  const canRemoveWatermark = !!(entitlements && entitlements.watermarkRemoval);
  const watermarkLocked = !canRemoveWatermark && !watermarkPending;

  // The one condition under which the mp4 comes out clean. A Free account's
  // switch cannot reach this, which is why it is gated on the entitlement and
  // not the draft. The switch presents its INVERSE: checked = the mark
  // renders, so Free (whose renders are always marked) sits pinned ON while
  // the stored setting stays `removeWatermark` (true = clean).
  const watermarkRemoved = canRemoveWatermark && !!draft.removeWatermark;
  const watermarkOn = !watermarkRemoved;
  const watermarkState = watermarkPending
    ? 'Checking…'
    : watermarkLocked
      ? WATERMARK_UNLOCK_PILL
      : watermarkOn ? 'On' : 'Off';

  const watermarkCornerWord = (
    WATERMARK_POSITION_LABELS[draft.watermarkPosition] || WATERMARK_POSITION_LABELS[WATERMARK_POSITION_DEFAULT]
  ).toLowerCase();
  const watermarkHint = watermarkPending
    ? 'Checking your plan…'
    : watermarkLocked
      ? 'Free renders include a small Tweedia Studio wordmark in the corner. Upgrading removes it — or swaps in your own logo.'
      : watermarkRemoved
        ? 'The wordmark is removed from this render.'
        : isWatermarkLogoId(draft.watermarkLogoId)
          // The id is a slot name (`logo2`), never a thing to show. The label
          // rides the draft from the tile that was picked; a draft rebuilt from
          // Firestore alone has none, so it says "Your logo".
          ? `${draft.watermarkLogoName || 'Your logo'} renders in the ${watermarkCornerWord} corner.`
          : `The Tweedia Studio wordmark renders in the ${watermarkCornerWord} corner.`;

  const openWatermarkUpsell = () => {
    trackEvent('watermark_upgrade_clicked', { project_id: projectId });
    setUpsellPrompt(UPSELL_PROMPTS.WATERMARK_LOCKED);
  };

  // Operation id -> card renderer. The render below filters on this map itself,
  // so an operation with no card yet is simply absent instead of rendering an
  // empty row — and there is no second list of ids to keep in sync.
  const CARDS = {
    // The Motion section. Keyed on `trim_stillness` because that is its slot in
    // the registry's pipeline order (MotionPace has no `copyKey`, so it gets no
    // card of its own — it is a MODE of this one), and because the card's ids
    // are what the accordion, the scroll-into-view refs and every existing test
    // address it by.
    trim_stillness: () => (
      <OperationCard
        key="trim_stillness"
        operationId="trim_stillness"
        name={motionName}
        state={trimState}
        stateTone={trimTone}
        expanded={selected === 'trim_stillness'}
        onSelect={() => select('trim_stillness')}
        rootRef={refFor('trim_stillness')}
        toggle={(
          <ToggleSwitch
            id="op-toggle-trim_stillness"
            checked={trimOn}
            onChange={setMotionEnabled}
            label={`${motionName} on or off`}
          />
        )}
      >
        {!trimOn ? (
          <OffReason operationId="trim_stillness">
            {motionPaceEnabled
              ? MOTION_SECTION.offReason
              : 'Nothing is trimmed — your video renders at full length.'}
          </OffReason>
        ) : motionPaceEnabled ? (
          <MotionSection
            draft={draft}
            onDraftChange={onDraftChange}
            onMotionCommit={persistMotion}
            mode={motionMode}
            video={video}
            projectId={projectId}
            isRecalculating={isRecalculating}
            motionControls={trimPanel}
            videoRef={videoPlayerRef}
            videoUrl={videoUrl}
          />
        ) : trimPanel}
      </OperationCard>
    ),

    deflicker: () => (
      <OperationCard
        key="deflicker"
        operationId="deflicker"
        name={displayNameOf('deflicker')}
        state={deflickerState}
        stateTone={deflickerOn ? 'on' : 'off'}
        note={!deflickerOn && flickerDetected ? 'Flicker detected' : null}
        expanded={selected === 'deflicker'}
        onSelect={() => select('deflicker')}
        rootRef={refFor('deflicker')}
        toggle={(
          <ToggleSwitch
            id="op-toggle-deflicker"
            checked={deflickerOn}
            onChange={(checked) => patch('deflicker', { deflicker: checked })}
            label={`${displayNameOf('deflicker')} on or off`}
          />
        )}
      >
        {deflickerOn ? (
          <div className="op-card__body" data-testid="op-body-deflicker">
            <p>
              Smooths the frame-to-frame brightness flicker common in interval
              timelapses, while preserving gradual changes like sunsets.
            </p>
            {flickerDetected && (
              <p className="op-card__detected" data-testid="deflicker-detected-detail">
                Flicker detected in this footage
                {flickerMagnitude != null ? ` (about ${formatSeconds(flickerMagnitude)}% frame-to-frame)` : ''}.
              </p>
            )}
            {/* Strength: the same three-tile radio group the transitions card
                uses, for the same reason — three named choices with their
                tradeoff spelled out beat a slider whose numbers mean nothing to
                a shooter. The real frame count rides along as secondary text,
                as the transitions tiles show their real length. */}
            <span className="op-card__field-label" id="deflicker-strength-label">
              Strength
            </span>
            <div
              role="radiogroup"
              aria-labelledby="deflicker-strength-label"
              className="op-card__presets"
              data-testid="deflicker-strength"
            >
              {DEFLICKER_WINDOW_CHOICES.map((frames) => {
                const strength = DEFLICKER_STRENGTHS[frames];
                const isSelected = frames === deflickerWindow;
                return (
                  <label
                    key={frames}
                    className={`preset-card${isSelected ? ' is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="deflicker-strength"
                      value={strength.id}
                      checked={isSelected}
                      onChange={() => patch('deflicker', { deflickerWindow: frames })}
                      className="preset-card__radio"
                    />
                    <span className="preset-card__top">
                      <span className="preset-card__label">{strength.label}</span>
                      <span className="preset-card__badge">{frames} frames</span>
                    </span>
                    <span className="preset-card__description">{strength.tradeoff}</span>
                  </label>
                );
              })}
            </div>
            {showFlickerReadout && (
              /* The one number a shooter actually wants: does this setting get
                 me under the line where flicker stops being visible. Both
                 figures come from the analysis at full frame rate, so they are
                 an estimate of the render and are labelled as one — the chart
                 below repeats that, and neither may imply a rendered A/B. */
              <p
                className="op-card__flicker-readout"
                data-testid="deflicker-strength-readout"
              >
                <strong>
                  Flicker {formatSeconds(flickerMagnitude)}% &rarr;{' '}
                  {formatSeconds(flickerMagnitudeAfter)}%
                </strong>{' '}
                at {DEFLICKER_STRENGTHS[deflickerWindow].label} strength —{' '}
                {/* Both branches name the SAME fact — the chart's own 1% line —
                    rather than promising an outcome. "Stops being noticeable"
                    read as a guarantee, and at Light this number lands on 0.99%
                    often enough that a guarantee is exactly what it must not
                    make. The threshold is a detection line, not a perceptual
                    one. */}
                {flickerMagnitudeAfter < FLICKER_DETECT_THRESHOLD_PERCENT
                  ? `under the ${FLICKER_DETECT_THRESHOLD_PERCENT}% mark where flicker becomes noticeable`
                  : `still above the ${FLICKER_DETECT_THRESHOLD_PERCENT}% mark where flicker becomes noticeable`}
                . Estimated from the analysis.
              </p>
            )}
            <p className="op-card__apply-note" data-testid="deflicker-strength-note">
              Strength is applied when you render. It does not change a video you
              have already rendered.
            </p>
            <FlickerPreview luminanceTrack={luminanceTrack} frameWindow={deflickerWindow} />
          </div>
        ) : (
          // The graph shows in the OFF state too, and that is the point of #405:
          // it is the only evidence a user has that switching this on is worth a
          // credit, and it costs nothing to draw either way.
          <div className="op-card__body" data-testid="op-body-deflicker-off">
            <OffReason operationId="deflicker">
              Brightness will be left exactly as shot.
              {flickerDetected ? ' This footage looks like it flickers — switching this on will smooth it.' : ''}
            </OffReason>
            {/* No Strength tiles here: a control for an operation that will not
                run is exactly what this card's panel rule exists to keep off
                the screen. The graph still draws at the saved strength, so
                switching the operation on shows the same chart it showed while
                it was off — the setting is remembered, not reset. */}
            <FlickerPreview luminanceTrack={luminanceTrack} frameWindow={deflickerWindow} />
          </div>
        )}
      </OperationCard>
    ),

    transitions: () => (
      <OperationCard
        key="transitions"
        operationId="transitions"
        name={displayNameOf('transitions')}
        state={transitionsState}
        stateTone={!transitionsOn ? 'off' : (singleClip || renderedCrossfades === 0) ? 'warn' : 'on'}
        expanded={selected === 'transitions'}
        onSelect={() => select('transitions')}
        rootRef={refFor('transitions')}
        toggle={(
          <ToggleSwitch
            id="op-toggle-transitions"
            checked={transitionsOn}
            onChange={(checked) => patch('transitions', {
              transitionFrames: checked ? defaultTransitionFrames : 0,
            })}
            label={`${displayNameOf('transitions')} on or off`}
          />
        )}
      >
        {transitionsOn ? (
          <div className="op-card__body">
            {singleClip && !paceNoteInChecklist && (
              <p className="op-card__reason" data-testid="op-note-transitions-single">
                {paceMode
                  ? PACE_NO_CUTS
                  : 'This edit is a single clip, so there are no joins to fade yet.'}
              </p>
            )}
            <div role="radiogroup" aria-label="Transition preset" className="op-card__presets">
              {presets.filter((p) => p.frames > 0).map((preset) => {
                const isSelected = !customActive && presetId === preset.id;
                return (
                  <label key={preset.id} className={`preset-card${isSelected ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="transition-preset"
                      value={preset.id}
                      checked={isSelected}
                      onChange={() => selectPreset(preset)}
                      className="preset-card__radio"
                    />
                    <span className="preset-card__top">
                      <span className="preset-card__label">{preset.label}</span>
                      <span className="preset-card__badge">{formatSeconds(preset.seconds)}s</span>
                    </span>
                    <span className="preset-card__description">{preset.description}</span>
                  </label>
                );
              })}
              <label
                className={`preset-card preset-card--custom${customActive ? ' is-selected' : ''}`}
                data-testid="transition-preset-custom"
              >
                <input
                  type="radio"
                  name="transition-preset"
                  value="custom"
                  checked={customActive}
                  onChange={() => setCustomSelected(true)}
                  className="preset-card__radio"
                />
                <span className="preset-card__top">
                  <span className="preset-card__label">Custom</span>
                  <span className="preset-card__badge">0&ndash;3s</span>
                </span>
                {customActive ? (
                  <span className="op-card__custom-inline">
                    <span className="numeric-input numeric-input--wide">
                      <input
                        type="number"
                        min={0}
                        max={3}
                        step={0.1}
                        value={customDraft !== undefined
                          ? customDraft
                          : formatSeconds(framesToSeconds(draft.transitionFrames, safeFps))}
                        onChange={handleCustomChange}
                        onBlur={handleCustomBlur}
                        className="setting-input setting-input--compact"
                        aria-label="Custom transition length in seconds"
                      />
                      <span className="numeric-input__suffix">s</span>
                    </span>
                    <span className="op-card__caption">
                      = {draft.transitionFrames} {draft.transitionFrames === 1 ? 'frame' : 'frames'} at {Math.round(safeFps)}fps
                    </span>
                  </span>
                ) : (
                  <span className="preset-card__description">Set your own length</span>
                )}
              </label>
            </div>
            {/* WHERE THAT LENGTH LANDS. One length, several kinds of boundary —
                the checklist names the ones this project has and says which of
                them the render fades. Motion cuts have no switch on the wire, so
                that row is inert; the other two write the same draft keys, and
                fire the same analytics operation ids, the sibling cards did. */}
            {showFadeChecklist && (
              <>
                <span
                  className="op-card__field-label op-card__field-label--fade-at"
                  id="transitions-fade-at-label"
                >
                  Fade at
                </span>
                <div
                  className="op-card__fade-at"
                  data-testid="transitions-fade-at"
                  role="group"
                  aria-labelledby="transitions-fade-at-label"
                >
                  {paceNoteInChecklist && (
                    <p className="op-card__fade-note" data-testid="op-note-transitions-pace">
                      {PACE_NO_CUTS}
                    </p>
                  )}
                  {showMotionCuts && (
                    <FadeRow
                      id="motion_cuts"
                      label="Motion cuts"
                      meta={`${motionCutCount} ${motionCutCount === 1 ? 'cut' : 'cuts'} where stillness is trimmed — always faded while Transitions are on`}
                      checked
                    />
                  )}
                  {hasCaptureGaps && (
                    <FadeRow
                      id="capture_gap_transitions"
                      // The NAME comes from the copy module (#379); the bracket
                      // is a scope note, not part of it — 4 hours is
                      // CAPTURE_GAP_MIN_SECONDS, the floor that promoted these
                      // frames in the first place (photo_compile.py).
                      label={`${OPERATION_COPY.capture_gap_transitions.displayName} (4+ hours)`}
                      meta={[
                        `${captureGaps.length} ${captureGaps.length === 1 ? 'gap' : 'gaps'} detected`,
                        captureGapsCovered ? 'already fading with your clip joins' : null,
                        // Only present when the render will skip some of them
                        // (#589); the all-fade case reads exactly as before.
                        fadeShortfall(gapTally, captureGapTransitionsOn || captureGapsCovered),
                      ].filter(Boolean).join(' — ')}
                      checked={captureGapTransitionsOn}
                      onChange={(checked) => patch('capture_gap_transitions', { captureGapTransitions: checked })}
                    />
                  )}
                  {hasClipJoins && (
                    <FadeRow
                      id="clip_join_transitions"
                      label={OPERATION_COPY.clip_join_transitions.displayName}
                      meta={[clipJoinWord, fadeShortfall(joinTally, clipJoinTransitionsOn)]
                        .filter(Boolean).join(' — ')}
                      checked={clipJoinTransitionsOn}
                      onChange={(checked) => patch('clip_join_transitions', { clipJoinTransitions: checked })}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          // The dependency, stated instead of demonstrated. The two sibling
          // switches used to sit here live but dead — this names what they would
          // have fixed, which is the one thing a user needs to decide whether to
          // turn the card back on.
          <OffReason operationId="transitions">
            Everything cuts straight from one clip to the next
            {offBoundaries.length > 0
              ? `, including your ${offBoundaries.join(' and ')}.`
              : '.'}
          </OffReason>
        )}
      </OperationCard>
    ),

    watermark_removal: () => (
      <OperationCard
        key="watermark_removal"
        operationId="watermark_removal"
        name={displayNameOf('watermark_removal')}
        state={watermarkState}
        stateTone={watermarkPending ? 'neutral' : watermarkLocked ? 'locked' : watermarkOn ? 'on' : 'off'}
        expanded={selected === 'watermark_removal'}
        onSelect={() => select('watermark_removal')}
        rootRef={refFor('watermark_removal')}
        affordance={watermarkLocked ? (
          <button
            type="button"
            className="op-card__upsell"
            onClick={openWatermarkUpsell}
            data-testid="watermark-upgrade"
          >
            See what removes it
          </button>
        ) : null}
        toggle={(
          <span className="op-card__switch-wrap">
            {/* Stays `disabled`, not merely aria-disabled: an inert input is what
                guarantees a Free account cannot toggle its way to
                removeWatermark:false. The affordance beside it only opens the
                prompt — it never changes the preference. */}
            <ToggleSwitch
              id="op-toggle-watermark_removal"
              // Checked = the mark RENDERS — the inverse of the stored
              // `removeWatermark` (true = clean). Free resolves to checked,
              // matching the marked render it will actually get.
              checked={watermarkOn}
              disabled={!canRemoveWatermark || watermarkPending}
              onChange={(checked) => {
                // Second guard behind `disabled`. jsdom (and any programmatic
                // click) will still dispatch change on a disabled input, and the
                // preference this writes is the value that goes on the wire —
                // so the inert row must be inert in the handler too.
                if (!canRemoveWatermark || watermarkPending) return;
                patch('watermark_removal', { removeWatermark: !checked });
              }}
              label={`${displayNameOf('watermark_removal')} on or off`}
            />
            {/* A disabled input swallows clicks, so "clicking the locked switch
                opens the upgrade modal" needs a real control on top of it. */}
            {watermarkLocked && (
              <button
                type="button"
                className="op-card__lock-hit"
                onClick={openWatermarkUpsell}
                data-testid="watermark-locked-switch"
                aria-label={`${displayNameOf('watermark_removal')} — upgrade required`}
              />
            )}
          </span>
        )}
      >
        <div
          className="op-card__body op-card__body--watermark"
          data-testid="op-body-watermark_removal"
        >
          <p data-testid="watermark-hint">{watermarkHint}</p>
          {!watermarkPending && (
            watermarkOn ? (
              <WatermarkCustomizationPanel
                projectId={projectId}
                videoUrl={videoUrl}
                resolution={video?.resolution}
                draft={draft}
                locked={watermarkLocked}
                onChange={(changes) => onDraftChange(changes)}
                onLockedClick={openWatermarkUpsell}
              />
            ) : (
              <>
                <WatermarkPreview
                  videoUrl={videoUrl}
                  resolution={video?.resolution}
                  showMark={false}
                />
                {/* Names the still, so it is not read as a player or as the
                    finished render. Tied to the preview so the pending panel
                    never captions a frame that is not there. */}
                <p
                  className="op-watermark-preview__caption"
                  data-testid="watermark-preview-caption"
                >
                  Preview &mdash; a frame from your own footage.
                </p>
              </>
            )
          )}
        </div>
      </OperationCard>
    ),
  };

  return (
    <section className="op-cards" data-testid="operation-cards" aria-label="Your edit">
      <p className="op-cards__intro" data-testid="op-cards-intro">
        Each step below is applied when you render — switch off anything you don't want.
      </p>
      {OPERATIONS_BY_UI_ORDER
        .filter((op) => CARDS[op.id])
        .map((op) => CARDS[op.id]())}
      {upsellPrompt && (
        <UpgradePromptDialog
          promptId={upsellPrompt}
          entitlements={entitlements}
          subscription={entitlements && entitlements.subscription}
          projectId={projectId}
          onTopUp={() => setUpsellPrompt(null)}
          onClose={() => setUpsellPrompt(null)}
        />
      )}
    </section>
  );
};

export default OperationCards;
